import { Grid, Typography, TextField, Stack, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import SingleDropDown from "components/Dropdowns/SingleDropDown/SingleDropDown";
import TitleDefinition from "components/TitleDefinition";
import { TitleDefinitionConstants } from "constants/constant";
import { CreateUpdateReductionMeasuresDesignProps } from "./data/CreateUpdateReductionMeasuresDesignProps";

/**
 * MeasureDetails component renders a form for entering and editing reduction measure details.
 *
 * @param {Readonly<CreateUpdateReductionMeasuresDesignProps>} props - The properties passed to the component.
 * @returns {JSX.Element} The MeasureDetails form.
 */

export default function MeasureDetails(
  props: Readonly<CreateUpdateReductionMeasuresDesignProps>
):JSX.Element {
  return (
    <>
      <Typography
        variant="h6"
        data-testid={"measureDetailsLabel"}
        sx={{ fontWeight: 600, fontSize: 12, mb: 2 }}
      >
        Measure Details
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="body1" data-testid={"reductionMeasureNameLabel"} sx={{ fontWeight: 600, fontSize: 12, mb: 0.1 }}>
            Name
            <Typography component="span" sx={{ color: "red", fontWeight: 600, fontSize: 12 }}>
              *
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0 }}>
            <TextField
              required
              id="reductionMeasureName"
              inputProps={{ "data-testid": "reductionMeasureName", style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
              fullWidth
              margin="none"
              size="small"
              name={"reductionMeasureName"}
              value={props.draftReductionMeasure.reductionMeasureName}
              onChange={(e) => {
                props.fieldChangeHandler(e);
              }}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
            />
          </Box>
          <Box sx={{ height: 12 }} />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant="body1"
            data-testid={"businessUnitLabel"}
            sx={{ fontWeight: 600, fontSize: 12, mb: 0.1 }}
          >
            Business Unit
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0 }}>
            <SingleDropDown
              onChange={props.fieldChangeHandler}
              list={props.businessUnitList}
              value={props.draftReductionMeasure.businessUnit}
              name={"businessUnit"}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
            />
          </Box>
          <Box sx={{ height: 12 }} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography variant="body1" data-testid={"dataSources"} sx={{ fontWeight: 600, fontSize: 12, mb: 0.1 }}>
              Data Sources
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.DataSources}
              placement="auto"
            />
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0 }}>
            <TextField
              id="dataSources"
              inputProps={{ "data-testid": "dataSources", style: { fontSize: 12, padding: '4px 6px', height: 30 } }}
              fullWidth
              margin="none"
              size="small"
              name={"dataSources"}
              value={props.draftReductionMeasure.dataSources}
              onChange={(e) => {
                props.fieldChangeHandler(e);
              }}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiInputBase-input': { height: 30, padding: '4px 6px', boxSizing: 'border-box' } }}
            />
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              data-testid={"measureStage"}
              sx={{ fontWeight: 600, fontSize: 12, mb: 0.1 }}
            >
              Stage
            </Typography>
            <TitleDefinition
              content={TitleDefinitionConstants.ReductionMeasure.Stage}
              placement="auto"
            />
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0 }}>
            <SingleDropDown
              onChange={props.fieldChangeHandler}
              list={props.measureStageList.map((g) => g.value)}
              value={props.draftReductionMeasure.measureStage}
              name={"measureStage"}
              sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
            <Typography
              variant="body1"
              data-testid={"measureCategoryLabel"}
              sx={{ mb: 1, fontWeight: 600, fontSize: 12 }}
            >
                {/* Add vertical space above Measure Category Label */}
                <Box sx={{ mt: '12px' }} />
                Measure Category
              <Typography component="span" sx={{ color: "red", fontWeight: 600, fontSize: 12 }}>
                *
              </Typography>
            </Typography>
            <a href="/glossary" target="_blank">
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "#e4610f", mx: 0.4, my: 0.2 }}
              />
            </a>
          </Stack>
          <SingleDropDown
            required
            onChange={props.fieldChangeHandler}
            list={props.measureCategoryList.map((g) => g.value)}
            value={props.draftReductionMeasure.measureCategory}
            name={"measureCategory"}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
          />
        </Grid>
        <Grid item xs={6} sx={{ mt: 1 }}>
          <Stack direction={"row"}>
              <Typography
                variant="body1"
                data-testid={"measureTypeLabel"}
                sx={{ mb: 0.1, fontWeight: 600, fontSize: 12 }}
              >
                {/* Add vertical space above Measure Type Label for alignment */}
                <Box sx={{ mt: '12px' }} />
                Measure Type
                <Typography component="span" sx={{ color: "red", fontWeight: 600, fontSize: 12 }}>
                  *
                </Typography>
              </Typography>
            <a href="/glossary" target="_blank">
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: "#e4610f", mx: 0.4, my: 0.2 }}
              />
            </a>
          </Stack>
          <Box sx={{ height: 12 }} />
          <SingleDropDown
            disabled={props.draftReductionMeasure.measureCategory.length < 1}
            required
            onChange={props.fieldChangeHandler}
            list={props.measureTypeList.map((g) => g.value)}
            value={props.draftReductionMeasure.measureType}
            name={"measureType"}
            sx={{ fontSize: 12, height: 30, minHeight: 30, boxSizing: 'border-box', '.MuiSelect-select': { display: 'flex', alignItems: 'center', height: 30, paddingTop: '4px', paddingBottom: '4px', fontSize: 12, boxSizing: 'border-box' } }}
          />
        </Grid>
      </Grid>
      <Grid>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            data-testid={"descriptionLabel"}
            sx={{ mt: 1, fontWeight: 600, fontSize: 12 }}
          >
            Description
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            multiline
            rows={3}
            inputProps={{
              maxLength: 300,
              "data-testid": "measureDescription",
              style: { fontSize: 12, padding: '4px 6px' }
            }}
            value={props.draftReductionMeasure.measureDescription}
            onChange={props.fieldChangeHandler}
            name={"measureDescription"}
            sx={{ fontSize: 12, boxSizing: 'border-box', '.MuiInputBase-input': { fontSize: 12, padding: '4px 6px', boxSizing: 'border-box' } }}
          />
        </Grid>
      </Grid>
    </>
  );
}
